import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { useSelectedDealer } from "components/DealersAndLocationsDropdown/hooks";
import { usePrevious } from "hooks";
import { downloadDealerLicenseInvoice, setPaginatedInvoices, useDealerInvoicesQuery } from "modules/DealerLicenseOverview/hooks";
import "modules/LocationLicenseOverview/LocationLicenseOverview.scss";

import type { HistoricalInvoice } from "modules/LocationLicenseOverview/queryKeys";
import type { Dispatch, SetStateAction } from "react";
import type { MenuItemProps } from "semantic-ui-react";

export enum DEALER_LICENSE_SUBPAGE {
  MonthlyLicenses = 0,
  OneTimeLicenses,
  Invoices
}

type DealerLicenseTabsProps = {
  setSelectedInvoice: Dispatch<SetStateAction<HistoricalInvoice | null>>;
  setInvoicePDF: Dispatch<SetStateAction<Blob | null>>;
};

export const useDealerLicenseTabs = ({ setSelectedInvoice, setInvoicePDF }: DealerLicenseTabsProps) => {
  const { t } = useTranslation();

  const { selectedDealer } = useSelectedDealer();
  const previousDealer = usePrevious(selectedDealer);

  const [paginatedURL, setPaginatedURL] = useState("");
  const [currentInvoicePage, setCurrentInvoicePage] = useState(0);
  const [currentLocationLicenseSubpage, setCurrentLocationLicenseSubpage] = useState<DEALER_LICENSE_SUBPAGE>(DEALER_LICENSE_SUBPAGE.MonthlyLicenses);

  const { dealerInvoices, paginatedDealerInvoices, isLoadingDealerInvoices, errorDealerInvoices, refetchInvoices, isDealerInvoicesRefetching } = useDealerInvoicesQuery(
    paginatedURL,
    currentInvoicePage
  );

  const onInvoiceRowClick = async (rowData: HistoricalInvoice) => {
    if (!selectedDealer?.id) return;

    setSelectedInvoice(rowData);
    try {
      const pdfData = await downloadDealerLicenseInvoice({ document_id: rowData.ID, dealer_id: selectedDealer.id });
      setInvoicePDF(pdfData);
    } catch {
      toast.error(t("v8_failed_to_open_invoice", "Failed to open invoice"));
    }
  };

  const handleChangeTab = (_e: React.MouseEvent<HTMLAnchorElement>, { index }: MenuItemProps) => {
    setCurrentLocationLicenseSubpage(index as DEALER_LICENSE_SUBPAGE);
  };

  const invoicePageChange = (pageIndex: number) => {
    if (pageIndex < 0) return;

    const existingDealerData = paginatedDealerInvoices.data.find(element => element.dealer === selectedDealer?.id);
    if (existingDealerData && Boolean(existingDealerData.data[pageIndex])) return setCurrentInvoicePage(pageIndex);

    if (!dealerInvoices?.__next) return;

    setPaginatedURL(dealerInvoices.__next);
    setCurrentInvoicePage(pageIndex);
  };

  const handleResetState = () => {
    setPaginatedURL("");
    setCurrentInvoicePage(0);
    setCurrentLocationLicenseSubpage(DEALER_LICENSE_SUBPAGE.MonthlyLicenses);
    setPaginatedInvoices({ data: [] });
  };

  useEffect(() => {
    if (selectedDealer && previousDealer && selectedDealer.id !== previousDealer.id) {
      setCurrentInvoicePage(0);
    }
  }, [selectedDealer, previousDealer]);

  return {
    refetchInvoices,
    handleResetState,
    handleChangeTab,
    invoicePageChange,
    onInvoiceRowClick,
    isDealerInvoicesRefetching,
    dealerInvoices,
    currentInvoicePage,
    errorDealerInvoices,
    isLoadingDealerInvoices,
    currentLocationLicenseSubpage
  };
};
